import React, { useState } from 'react';
import { Play, Video, BookOpen, Mic, Music, Trophy, Settings, X } from 'lucide-react';

interface TutorialVideo {
  id: string;
  title: string;
  description: string;
  category: string;
}

const PLAYLIST_ID = 'PL6IHWD7uJyz4WGUZcWYmlzeH6UT_LU-rv';

const TUTORIAL_VIDEOS: TutorialVideo[] = [
  {
    id: 'VIDEO_1',
    title: 'Getting Started with The Singing League',
    description: 'Learn how to create your account, set up your profile, and navigate the app.',
    category: 'Getting Started',
  },
  {
    id: 'VIDEO_2',
    title: 'How to Record Your First Performance',
    description: 'Step-by-step guide to recording your first song, including microphone setup and tips.',
    category: 'Recording',
  },
  {
    id: 'VIDEO_3',
    title: 'Understanding Your Performance Score',
    description: 'Learn how the AI scoring system works and what each metric means.',
    category: 'Scoring',
  },
  {
    id: 'VIDEO_4',
    title: 'Joining and Competing in Tournaments',
    description: 'Everything you need to know about entering tournaments and climbing the bracket.',
    category: 'Tournaments',
  },
  {
    id: 'VIDEO_5',
    title: 'Using Vocal FX and the AI Studio',
    description: 'Discover how to enhance your recordings with vocal effects and AI tools.',
    category: 'Features',
  },
  {
    id: 'VIDEO_6',
    title: 'Tips for Better Recordings',
    description: 'Pro tips on microphone technique, room setup, and getting the best sound quality.',
    category: 'Recording',
  },
];

const CATEGORIES = ['All', 'Getting Started', 'Recording', 'Scoring', 'Tournaments', 'Features'];

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  'All': <Video size={16} />,
  'Getting Started': <BookOpen size={16} />,
  'Recording': <Mic size={16} />,
  'Scoring': <Music size={16} />,
  'Tournaments': <Trophy size={16} />,
  'Features': <Settings size={16} />,
};

export const Academy: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [playingInModal, setPlayingInModal] = useState(false);

  const filteredVideos = activeCategory === 'All'
    ? TUTORIAL_VIDEOS
    : TUTORIAL_VIDEOS.filter(v => v.category === activeCategory);

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Video Tutorials</h1>
        <p className="text-white/70">Learn how to get the most out of The Singing League with our video guides.</p>
      </div>

      {/* Featured Playlist Player */}
      <div className="bg-kod-purple-800 rounded-xl border border-kod-purple-600/40 shadow-lg overflow-hidden mb-10">
        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/videoseries?list=${PLAYLIST_ID}&rel=0`}
            title="The Singing League Tutorials"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <div className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">The Singing League - How To</h2>
            <p className="text-white/60 text-sm">Browse through all tutorial videos in the playlist above.</p>
          </div>
          <button
            onClick={() => setPlayingInModal(true)}
            className="flex items-center gap-2 bg-kod-accent hover:bg-kod-accent-dark text-white px-4 py-2.5 rounded-lg transition-colors text-sm font-medium whitespace-nowrap"
          >
            <Play size={16} />
            Watch Full Screen
          </button>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2 mb-8">
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${
              activeCategory === cat
                ? 'bg-kod-accent text-white shadow-lg shadow-kod-accent/20'
                : 'bg-kod-purple-700/50 text-white/70 hover:bg-kod-purple-700 hover:text-white border border-kod-purple-600/40'
            }`}
          >
            {CATEGORY_ICONS[cat]}
            {cat}
          </button>
        ))}
      </div>

      {/* Video Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredVideos.map((video, index) => (
          <VideoCard key={video.id} video={video} index={index} />
        ))}
      </div>

      {/* Quick Tips Section */}
      <div className="mt-12 bg-kod-purple-800/50 rounded-xl border border-kod-purple-600/40 p-6">
        <h3 className="text-xl font-semibold mb-4">Quick Tips</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-kod-purple-700/40 rounded-lg p-4 border border-kod-purple-600/30">
            <div className="text-kod-accent font-semibold text-sm mb-2">Headphones Recommended</div>
            <p className="text-white/60 text-sm">For the best recording quality, always use headphones to prevent the backing track from bleeding into your microphone.</p>
          </div>
          <div className="bg-kod-purple-700/40 rounded-lg p-4 border border-kod-purple-600/30">
            <div className="text-kod-accent font-semibold text-sm mb-2">Quiet Environment</div>
            <p className="text-white/60 text-sm">Find a quiet space to record. Background noise can affect your performance score and audio quality.</p>
          </div>
          <div className="bg-kod-purple-700/40 rounded-lg p-4 border border-kod-purple-600/30">
            <div className="text-kod-accent font-semibold text-sm mb-2">Practice First</div>
            <p className="text-white/60 text-sm">Listen to the song a few times before recording. Familiarity with the melody will boost your score.</p>
          </div>
        </div>
      </div>

      {/* Fullscreen Modal */}
      {playingInModal && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
          <button
            onClick={() => setPlayingInModal(false)}
            className="absolute top-4 right-4 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors z-10"
          >
            <X size={24} />
          </button>
          <div className="w-full max-w-5xl">
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute inset-0 w-full h-full rounded-lg"
                src={`https://www.youtube.com/embed/videoseries?list=${PLAYLIST_ID}&autoplay=1&rel=0`}
                title="The Singing League Tutorials - Full Screen"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const VideoCard: React.FC<{ video: TutorialVideo; index: number }> = ({ video, index }) => {
  const thumbnailColors = [
    'from-blue-600/30 to-kod-purple-800',
    'from-emerald-600/30 to-kod-purple-800',
    'from-amber-600/30 to-kod-purple-800',
    'from-rose-600/30 to-kod-purple-800',
    'from-cyan-600/30 to-kod-purple-800',
    'from-orange-600/30 to-kod-purple-800',
  ];

  return (
    <div className="bg-kod-purple-800 rounded-xl border border-kod-purple-600/40 overflow-hidden hover:border-kod-accent/40 transition-all hover:shadow-lg hover:shadow-kod-accent/5 group">
      <div className={`relative h-40 bg-gradient-to-br ${thumbnailColors[index % thumbnailColors.length]} flex items-center justify-center`}>
        <div className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/20 group-hover:scale-110 transition-all">
          <Play size={24} className="text-white ml-1" />
        </div>
        <div className="absolute top-3 left-3">
          <span className="bg-black/40 backdrop-blur-sm text-white/90 text-xs px-2.5 py-1 rounded-full">
            {video.category}
          </span>
        </div>
        <div className="absolute bottom-3 right-3">
          <span className="bg-black/40 backdrop-blur-sm text-white/90 text-xs px-2.5 py-1 rounded-full">
            Tutorial {index + 1}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-sm mb-1.5 group-hover:text-kod-accent transition-colors line-clamp-2">
          {video.title}
        </h3>
        <p className="text-white/60 text-xs leading-relaxed line-clamp-2">
          {video.description}
        </p>
      </div>
    </div>
  );
};
